// src/templates/EcommerceTemplate.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  Search, 
  Filter,
  Grid,
  List,
  SlidersHorizontal,
  ShoppingCart,
  Heart,
  User,
  Menu,
  X,
  MapPin,
  Phone,
  Mail,
  Star,
  Package,
  Truck,
  CreditCard,
  Shield,
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  Gift,
  Percent,
  Eye,
  Share2,
  Download,
  ExternalLink,
  MessageCircle,
  Zap
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { ProductGrid } from '../ecommerce/ProductGrid';
import { ProductFilters } from '../ecommerce/ProductFilters';
import { CartSidebar } from '../ecommerce/CartSidebar';
import { ProductModal } from '../ecommerce/ProductModal';
import { CategoryNavigation } from '../ecommerce/CategoryNavigation';
import { FeaturedSection } from '../ecommerce/FeaturedSection';
import { HeroSection } from '../ecommerce/HeroSection';
import { PromoSection } from '../ecommerce/PromoSection';
import { WishlistModal } from '../ecommerce/WishlistModal';
import { SearchModal } from '../ecommerce/SearchModal';
import { ContactModal } from '../ecommerce/ContactModal';
import { ShareModal } from '../books/modals/ShareModal';

import { EcommerceManager } from '../ecommerce/EcommerceManager';
import { getStoreTypeConfig } from '../ecommerce/utils/storeTypeConfig';

import { SharedHeader } from '../shared/SharedHeader';
import { useCart } from '../../contexts/CartContext';
import { BookingSidebar } from '../ecommerce/BookingSidebar'; 
import { useBooking } from '../../contexts/BookingContext';

import { ServiceGrid } from '../ecommerce/ServiceGrid';
import { ServiceFilters } from '../ecommerce/ServiceFilters'
import { ServiceOptionsModal } from '../ecommerce/ServiceOptionsModal';

import toast from 'react-hot-toast';

export const EcommerceTemplate = ({ 
  user, 
  bookData, 
  bookId, 
  customStyling = {},
  viewManager,
  }) => {
  const { cart, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal, getCartItemCount } = useCart();
  const navigate = useNavigate();
  // State Management
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [storeSettings, setStoreSettings] = useState({});
  const { viewMode, isAuthenticated, hasManagementAccess } = viewManager || { viewMode: 'public', isAuthenticated: false, hasManagementAccess: false };
  const [isOwner, setIsOwner] = useState(false);
  // UI State
  const [storeView, setStoreView] = useState('grid'); // grid, list, masonry
  const [showFilters, setShowFilters] = useState(false)
  const [showBookingSidebar, setShowBookingSidebar] = useState(false);;
  const [showCart, setShowCart] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Filters and Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 99999]);
  const [sortBy, setSortBy] = useState('featured'); // featured, price-asc, price-desc, name, rating, newest
  const [filters, setFilters] = useState({
    inStock: false,
    onSale: false,
    featured: false,
    rating: 0,
    brands: [],
    tags: []
  });
  
  // Product Modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  
  const storeType = bookData?.storeType || storeSettings?.storeType || 'general';
  const isServiceStore = storeType === 'services';
  const storeConfig = useMemo(() => getStoreTypeConfig(storeType), [storeType]);
  const { 
    bookings, 
    addBooking, 
    removeBooking,
    clearBookings,
    getBookingsCount, 
    getBookingsTotal 
  } = useBooking();

  const [showServiceOptionsModal, setShowServiceOptionsModal] = useState(false);
  const [serviceForOptions, setServiceForOptions] = useState(null);
  const [selectedServiceOptions, setSelectedServiceOptions] = useState({});

  const layoutSettings = bookData?.layoutSettings || {};
  const isOverlayMode = layoutSettings.headerHeroIntegration === 'overlay';

  useEffect(() => {
    setStoreView(storeType === 'services' ? 'list' : 'grid');
  }, [storeType]);

  // Load store data
  useEffect(() => {
    if (viewMode === 'management') {
        setLoading(false);
        return;
      }
    loadStoreData();
    loadSectionData();
  }, [bookId, user, viewMode]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFilters();
  }, [products, searchQuery, selectedCategory, selectedSubcategory, priceRange, sortBy, filters]);

  const loadStoreData = async () => {
    setLoading(true);
    try {
      // Load store settings
      const settings = await ecommerceService.getStore(bookId);
      setStoreSettings(settings);
      
      // Load products
      const productsData = await ecommerceService.getProducts(bookId);
      setProducts(productsData);
      
      // Load categories
      const categoriesData = await ecommerceService.getCategories(bookId);
      setCategories(categoriesData);
      
      // Check ownership
      setIsOwner(user?.uid === bookData?.ownerId);
      
    } catch (error) {
      console.error('Error loading store data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSectionData = async () => {
    try {
      const sectionsData = await ecommerceService.getStoreSections(bookId);
      setStoreSettings(prevSettings => ({
        ...prevSettings,
        hero: sectionsData.hero,
        featured: sectionsData.featured,
        promos: sectionsData.promos,
        promotions: sectionsData.promotions
      }));
    } catch (error) {
      console.error('Error loading sections data:', error);
    }
  };

  useEffect(() => {
    if (selectedCategory !== 'all') {
      const category = categories.find(cat => cat.id === selectedCategory);
      const subcategories = category?.subcategories || [];
      setAvailableSubcategories(subcategories);
      
      // Reset subcategory if it doesn't exist in new category
      if (selectedSubcategory !== 'all') {
        const subcategoryExists = subcategories.some(sub => sub.id === selectedSubcategory);
        if (!subcategoryExists) {
          setSelectedSubcategory('all');
        }
      }
    } else {
      setAvailableSubcategories([]);
      setSelectedSubcategory('all');
    }
  }, [selectedCategory, categories]);

  const applyFilters = () => {
    let filtered = [...products];
    
    // Search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        product.brand?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    // Subcategory filter - ADD THIS NEW SECTION
    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter(product => product.subcategory === selectedSubcategory);
    }
    
    // Price range
    filtered = filtered.filter(product => {
      const price = product.salePrice || product.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Stock filter
    if (filters.inStock) {
      filtered = filtered.filter(product => product.inStock && product.quantity > 0);
    }
    
    // Sale filter
    if (filters.onSale) {
      filtered = filtered.filter(product => product.salePrice && product.salePrice < product.price);
    }
    
    // Featured filter
    if (filters.featured) {
      filtered = filtered.filter(product => product.featured);
    }
    
    // Rating filter
    if (filters.rating > 0) {
      filtered = filtered.filter(product => (product.rating || 0) >= filters.rating);
    }
    
    // Brand filter
    if (filters.brands.length > 0) {
      filtered = filtered.filter(product => filters.brands.includes(product.brand));
    }
    
    // Tags filter
    if (filters.tags.length > 0) {
      filtered = filtered.filter(product => 
        product.tags?.some(tag => filters.tags.includes(tag))
      );
    }
    
    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return (a.salePrice || a.price || 0) - (b.salePrice || b.price || 0);
        case 'price-desc':
          return (b.salePrice || b.price || 0) - (a.salePrice || a.price || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'featured':
        default:
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      }
    });
    
    setFilteredProducts(filtered);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
    
    // Track view
    ecommerceService.trackProductView(bookId, product.id);
  };

  const handleAddToCart = async (product, quantity = 1, variant = null) => {
    try {
      await addToCart(product, quantity, variant);
      
      // Track add to cart event
      ecommerceService.trackAddToCart(bookId, product.id, quantity);
      
      // Show success feedback
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  const handleContact = (type, details) => {
    switch (type) {
      case 'whatsapp':
        const message = `Hi! I'm interested in your products from ${storeSettings.storeName || 'your store'}. Can you help me?`;
        const whatsappUrl = `https://wa.me/${details}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        break;
      case 'email':
        const emailSubject = `Inquiry about ${storeSettings.storeName || 'your store'}`;
        const emailBody = `Hi,\n\nI'm interested in your products. Please get in touch with me.\n\nBest regards`;
        window.location.href = `mailto:${details}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        break;
      case 'phone':
        window.location.href = `tel:${details}`;
        break;
    }
    
    setShowContact(false);
  };

  const handleServiceClick = (service) => {
    console.log('📋 Service clicked:', service.name);
    
    // ✅ UPDATED: Check for optionGroups instead of options
    if (isServiceStore && service.optionGroups && service.optionGroups.length > 0) {
      // Open options modal
      console.log('⚙️ Opening service options modal');
      setServiceForOptions(service);
      setSelectedServiceOptions({});
      setShowServiceOptionsModal(true);
    } else {
      // Open regular product modal or direct book
      setSelectedProduct(service);
      setShowProductModal(true);
    }
  };

  const handleBookService = (bookingData) => {
    console.log('🎫 handleBookService called', bookingData);
    
    if (!bookingData || !bookingData.service) {
      console.error('❌ Invalid booking data:', bookingData);
      toast.error('Invalid booking data');
      return;
    }

    const { service, selectedOptions = [] } = bookingData;
    
    addBooking(service, selectedOptions);
    toast.success('Service added to bookings!');
    setShowBookingSidebar(true);
    
    // ✅ Close options modal if open
    setShowServiceOptionsModal(false);
    setServiceForOptions(null);
  };

  // ✅ ADD: Handle service option change in template
  const handleServiceOptionChange = (optionKey, quantity) => {
    console.log('📝 Option change:', { optionKey, quantity });
    
    setSelectedServiceOptions(prev => ({
      ...prev,
      [optionKey]: quantity // key format: "groupIndex-optionIndex"
    }));
  };

  // ✅ CORRECT: Book with selected options from optionGroups
 
  const handleBookServiceWithOptions = () => {
    if (!serviceForOptions) return;

    console.log('🔍 Service structure:', {
      name: serviceForOptions.name,
      optionGroups: serviceForOptions.optionGroups,
      selectedOptions: selectedServiceOptions
    });

    const selectedOptionsArray = [];
    
    // ✅ Process optionGroups - include groupIndex for BookingContext calculation
    if (serviceForOptions.optionGroups && serviceForOptions.optionGroups.length > 0) {
      serviceForOptions.optionGroups.forEach((group, groupIndex) => {
        if (!group.options || group.options.length === 0) return;
        
        group.options.forEach((option, optionIndex) => {
          const key = `${groupIndex}-${optionIndex}`;
          const quantity = selectedServiceOptions[key] || 0;
          
          if (quantity > 0) {
            selectedOptionsArray.push({
              name: option.name,
              groupName: group.name,
              description: option.description || '',
              price: option.price || 0,
              isFree: option.isFree || false,
              quantity: quantity,
              groupIndex: groupIndex, // ✅ CRITICAL: Include groupIndex for complementary calculation
              optionIndex: optionIndex
            });
          }
        });
      });
    }

    console.log('📋 Final selectedOptions with groupIndex:', selectedOptionsArray);

    handleBookService({
      service: serviceForOptions,
      selectedOptions: selectedOptionsArray
    });
    // Close modal and reset
    setShowServiceOptionsModal(false);
    setServiceForOptions(null);
    setSelectedServiceOptions({});
  };

  const handleContactAboutService = (service) => {
    setSelectedProduct(service);
    setShowContact(true);
  };


  const calculateHeaderHeight = () => {
    const logoHeight = bookData?.logoHeight || 40;
    const minHeaderHeight = 80; // Minimum header height in pixels
    const padding = 10; // Top and bottom padding combined
    return Math.max(minHeaderHeight, logoHeight + padding);
  };

  const storeTypeConfig = useMemo(() => {
    return getStoreTypeConfig(bookData?.storeType || storeSettings?.storeType || 'general');
  }, [bookData?.storeType, storeSettings?.storeType]);

  const headerHeight = calculateHeaderHeight();

  const Header = ({ isOwner, bookData, customStyling, navigate }) => {
    const [isSquare, setIsSquare] = useState(true);

    const handleImageLoad = (e) => {
      const { naturalWidth, naturalHeight } = e.target;
      setIsSquare(naturalWidth === naturalHeight);
    };

    // Get logo height from storeSettings or use default
    const logoHeight = bookData?.logoHeight || 40;

    return (
      <div className="flex items-center gap-2">
        {/* Return Arrow for Album/Book Owner */}
        {isOwner && (
          <button
            onClick={() => navigate("/dashboard")}
            className="p-0 hover:bg-gray-100 rounded-lg transition-colors"
            title="Return to Dashboard"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
        )}

        {/* Logo Display with Dynamic Height */}
        {bookData?.logoUrl && (
          <div className="flex-shrink-0">
            <img
              src={bookData.logoUrl}
              alt={`${bookData.title || bookData.botName} logo`}
              style={{ height: `${logoHeight}px` }}
              className="w-auto object-contain rounded-lg"
              onLoad={handleImageLoad}
            />
          </div>
        )}

        {/* Title Section (only show if logo is square OR no logo at all) */}
        {(isSquare || !bookData?.logoUrl) && (
          <div className="min-w-0">
            <h1
              className="text-2xl font-bold text-gray-900 truncate"
              style={{ color: defaultStyling.primaryColor }}
            >
              {bookData.title || bookData.botName}
            </h1>
            {bookData.description && (
              <p className="text-gray-600 mt-1 text-sm line-clamp-2">
                {bookData.description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Custom styling with defaults
  const defaultStyling = {
    primaryColor: '#3b82f6',
    accentColor: '#f59e0b',
    backgroundColor: '#ffffff',
    headerColor: '#ffffff',
    headerTextColor: '#1f2937',
    cardRadius: '12px',
    fontFamily: 'Inter, sans-serif'
  };
  
  const styling = { ...defaultStyling, ...customStyling };
  const design = bookData.design;
  const primaryColor = design.primaryColor || '#3b82f6';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (viewMode === 'management' && hasManagementAccess) {
    return (
      <>
        <EcommerceManager
          user={user}
          bookId={bookId}
          bookData={bookData}
          onUpdate={loadStoreData}
          
          customStyling={customStyling}
        />
        
      </>
    );
  }  

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Use SharedHeader with cart count passed as prop */}
      <SharedHeader
        templateType="ecommerce"
        bookData={bookData}
        styling={styling}
        isOwner={user?.uid === bookData.userId}
        navigate={navigate}
        headerConfig={{
          showLogo: true,
          showTitle: true,
          showSearch: true,
          showCart: true,
          showWishlist: true,
          showContact: true,
          showCategoryNav: true
        }}
        // Ecommerce-specific props
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        cartItemCount={isServiceStore ? getBookingsCount() : getCartItemCount()}
        wishlistCount={0} 
        onCartClick={() => isServiceStore ? setShowBookingSidebar(true) : setShowCart(true)}
        onWishlistClick={() => setShowWishlist(true)}
        onContactClick={() => setShowContact(true)}
        onSearchClick={() => setShowSearch(true)}
        storeType={storeType}
        state={{
          showFilters,
          storeView
        }}
        actions={{
          onstoreViewChange: setStoreView
        }}
      />

      {/* Hero Section */}
      {layoutSettings.sectionVisibility?.hero !== false && (
        <HeroSection 
          settings={storeSettings.hero} 
          styling={styling}
          onCTAClick={() => {
            document.getElementById('products-section')?.scrollIntoView({ 
              behavior: 'smooth' 
            });
          }}
        />
      )}

      {/* Category Navigation */}
      {categories.length > 0 && (
        <CategoryNavigation
          bookData={bookData}
          categories={categories}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          onCategorySelect={setSelectedCategory}
          onSubcategorySelect={setSelectedSubcategory}
          styling={styling}
          showSubcategories={true} 
          layout="horizontal"
          layoutSettings={layoutSettings}
          headerHeight={headerHeight}
        />
      )}

      {/* Promotional Section */}
      {storeSettings.design?.showPromos && storeSettings.promos?.enabled && storeSettings.promotions?.length > 0 && (
        <PromoSection 
          promotions={storeSettings.promotions}
          styling={styling}
          settings={storeSettings.promos}
        />
      )}

      {/* Featured Products */}
      {storeSettings.design?.showFeatured && storeSettings.featured?.enabled && (
        <FeaturedSection
          products={products.filter(p => p.featured).slice(0, storeSettings.featured?.displayCount || 8)}
          onProductClick={handleProductClick}
          onAddToCart={handleAddToCart}
          styling={styling}
          settings={storeSettings.featured}
        />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          className={`lg:grid lg:gap-8 ${
            bookData.sectionVisibility?.filters !== false
              ? 'lg:grid-cols-12'
              : 'flex justify-center'
          }`}
        >
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">

              {bookData.sectionVisibility?.filters !== false && (
                isServiceStore ? (
                  <ServiceFilters
                    services={products}
                    categories={categories}
                    filters={filters}
                    priceRange={priceRange}
                    onFiltersChange={setFilters}
                    onPriceRangeChange={setPriceRange}
                    styling={styling}
                  />
                ) : (
                  <ProductFilters
                    products={products}
                    categories={categories}
                    filters={filters}
                    priceRange={priceRange}
                    onFiltersChange={setFilters}
                    onPriceRangeChange={setPriceRange}
                    styling={styling}
                  />
                )
              )}

            </div>
          </div>

          {/* Products Section */}
          <div
            className={`${
              bookData.sectionVisibility?.filters !== false
                ? 'lg:col-span-9'
                : 'w-full max-w-5xl'
            }`}
            id="products-section"
          >
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategory === 'all' 
                    ? `All ${storeConfig.itemNamePlural}` 
                    : categories.find(c => c.id === selectedCategory)?.name || storeConfig.itemNamePlural
                  }
                </h2>
                <p className="text-gray-600">
                  {filteredProducts.length} {filteredProducts.length === 1 ? storeConfig.itemName.toLowerCase() : storeConfig.itemNamePlural.toLowerCase()}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Mobile Filters Button */}
                <button
                  onClick={() => setShowFilters(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                </button>

                {/* Sort Dropdown */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="featured">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="name">Name A-Z</option>
                  <option value="rating">Rating</option>
                </select>

                {/* View Mode Toggle */}
              
                <div className="hidden sm:flex border border-gray-300 rounded-lg overflow-hidden">
                 <button
                    onClick={() => setStoreView('grid')}
                    className={`p-2 ${storeView === 'grid' ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    style={{
                      backgroundColor: storeView === 'grid' ? primaryColor : undefined,
                    }}
                  >
                    <Grid className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setStoreView('list')}
                    className={`p-2 ${storeView === 'list' ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    style={{
                      backgroundColor: storeView === 'list' ? primaryColor : undefined,
                    }}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Grid/List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading {storeConfig.itemNamePlural.toLowerCase()}...</p>
                </div>
              </div>
            ) : filteredProducts.length > 0 ? (
              isServiceStore ? (
                <ServiceGrid
                  bookData={bookData}
                  services={filteredProducts}
                  storeView={storeView}
                  onServiceClick={handleServiceClick}
                  onBookService={handleBookService}
                  onContactAboutService={handleContactAboutService}
                  styling={styling}
                  storeSettings={storeSettings}
                />
              ) : (
                // Product Grid
                <ProductGrid
                  bookData={bookData}
                  products={filteredProducts}
                  storeView={storeView}
                  onProductClick={handleProductClick}
                  onAddToCart={handleAddToCart}
                  styling={styling}
                  storeSettings={storeSettings}
                />
              )
            ) : (
              <div className="text-center py-16">
                <Package className="mx-auto h-20 w-20 text-gray-300 mb-4" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">
                  {storeConfig.emptyMessage}
                </h3>
                <p className="text-gray-500">
                  {searchQuery || selectedCategory !== 'all' 
                    ? `No ${storeConfig.itemNamePlural.toLowerCase()} match your filters` 
                    : storeConfig.emptyMessageDetail
                  }
                </p>
              </div>
            )}

            {/* Empty State */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <ShoppingBag className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No products found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery 
                    ? `No products match your search "${searchQuery}"`
                    : 'Try adjusting your filters to see more products'
                  }
                </p>
                {(searchQuery || Object.values(filters).some(Boolean)) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilters({
                        inStock: false,
                        onSale: false,
                        featured: false,
                        rating: 0,
                        brands: [],
                        tags: []
                      });
                      setSelectedCategory('all');
                      setPriceRange([0, 99999]);
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Store Footer */}
      <footer className="border-t mt-16" style={{ backgroundColor: customStyling.headerColor }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Store Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {storeSettings.logoUrl && (
                  <img 
                    src={storeSettings.logoUrl} 
                    className="h-12 w-auto object-contain filter invert-[0.5]"
                  />
                )}
              </div>
              
              {storeSettings.description && (
                <p className="text-gray-600 mb-4"
                style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                >{storeSettings.description}</p>
              )}
              
              {/* Trust Badges */}
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  Secure
                </div>
                <div className="flex items-center gap-1">
                  <Truck className="h-4 w-4" />
                  Fast Shipping
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-4" 
              style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
              >Contact Us</h4>
              <div className="space-y-3">
                {storeSettings.phone && (
                  <div className="flex items-center gap-2 "
                  style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                  >
                    <Phone className="h-4 w-4" />
                    <span>{storeSettings.phone}</span>
                  </div>
                )}
                {storeSettings.email && (
                  <div className="flex items-center gap-2"
                  style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                  >
                    <Mail className="h-4 w-4" />
                    <span>{storeSettings.email}</span>
                  </div>
                )}
                {storeSettings.address && (
                  <div className="flex items-center gap-2"
                  style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                  >
                    <MapPin className="h-4 w-4" />
                    <span>{storeSettings.address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4" style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}>Quick Links</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => setShowContact(true)}
                  className="block hover:text-gray-900"
                  style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                >
                  Contact Us
                </button>
                <button 
                  onClick={() => setShowCart(true)}
                  className="block hover:text-gray-900"
                  style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                >
                  View Cart
                </button>
                <button 
                  onClick={() => setShowWishlist(true)}
                  className="block hover:text-gray-900"
                  style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }}
                >
                  Wishlist
                </button>
              </div>
            </div>

            {/* Business Hours / Additional Info */}
            <div>
              <h4 className="font-semibold mb-4" style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }} >Store Hours</h4>
              {storeSettings.businessHours ? (
                <div className="space-y-1 text-sm text-gray-600">
                  {Object.entries(storeSettings.businessHours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between">
                      <span className="capitalize">{day}:</span>
                      <span>{hours || 'Closed'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-md" style={{ color: bookData.headerTextColor || defaultStyling.headerTextColor }} > Contact us for availability</p>
              )}
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t mt-8 pt-8 flex flex-col sm:flex-row justify-center items-center">
            <p className="text-gray-600 text-sm" style={{ color: bookData?.design.accentColor }}>
              © 2025 {storeSettings.storeName || 'Store'}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals and Sidebars */}
      {isServiceStore ? (
        <BookingSidebar
          isOpen={showBookingSidebar}
          onClose={() => setShowBookingSidebar(false)}
          bookings={bookings}
          onRemoveBooking={removeBooking}
          onClearBookings={clearBookings}
          onContact={handleContact}
          storeSettings={storeSettings}
          styling={styling}
        />
      ) : (
        <CartSidebar
          isOpen={showCart}
          onClose={() => setShowCart(false)}
          cart={cart}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeFromCart}
          onClearCart={clearCart}
          onContact={handleContact}
          storeSettings={storeSettings}
          styling={styling}
        />
      )}

      {showServiceOptionsModal && serviceForOptions && (
        <ServiceOptionsModal
          service={serviceForOptions}
          selectedOptions={selectedServiceOptions}
          onOptionChange={handleServiceOptionChange}
          onClose={() => {
            setShowServiceOptionsModal(false);
            setServiceForOptions(null);
            setSelectedServiceOptions({});
          }}
          onBook={handleBookServiceWithOptions}
          styling={styling}
        />
      )}

      {showProductModal && selectedProduct && (
        <ProductModal
          product={selectedProduct}
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setSelectedProduct(null);
          }}
          onAddToCart={isServiceStore ? handleBookService : handleAddToCart}
          storeSettings={storeSettings}
          styling={styling}
        />
      )}

      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className="fixed inset-y-0 left-0 w-full max-w-sm bg-white">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <ProductFilters
                products={products}
                categories={categories}
                filters={filters}
                priceRange={priceRange}
                onFiltersChange={setFilters}
                onPriceRangeChange={setPriceRange}
                styling={styling}
                onClose={() => setShowFilters(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showSearch && (
        <SearchModal
          isOpen={showSearch}
          onClose={() => setShowSearch(false)}
          products={products}
          onProductSelect={handleProductClick}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      )}

      {showWishlist && (
        <WishlistModal
          isOpen={showWishlist}
          onClose={() => setShowWishlist(false)}
          onProductClick={handleProductClick}
          onAddToCart={handleAddToCart}
        />
      )}

      {showContact && (
        <ContactModal
          isOpen={showContact}
          onClose={() => setShowContact(false)}
          storeSettings={storeSettings}
          onContact={handleContact}
        />
      )}

      {showShare && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          bookData={bookData}
          bookId={bookId}
        />
      )}
    </div>
  );
};